/**
 * The work order process as six steps, left to right.
 *
 * On the list, each step shows how many orders are at it now and lists them
 * when clicked. On an order's own page, the step it is at is lit and the
 * next step is spelled out — what happens and who does it.
 */
import { FLOW, SIDE, nextStep } from "../../domain/workOrderFlow";
import { STATUS_LABEL, type WorkOrderStatus } from "../../domain/workOrders";
import { t } from "../../i18n";
import f from "./WorkOrderFlow.module.css";

export function WorkOrderFlow({
  counts,
  current,
  active,
  onPick,
}: {
  /** orders at each step now (the list) */
  counts?: Partial<Record<WorkOrderStatus, number>>;
  /** the step this order is at (an order's page) */
  current?: WorkOrderStatus;
  /** the step the list is filtered to */
  active?: string | null;
  onPick?: (status: WorkOrderStatus) => void;
}) {
  const at = current ? FLOW.findIndex((s) => s.status === current) : -1;
  const next = current ? nextStep(current) : null;
  const Step = onPick ? "button" : "div";
  return (
    <section className={f.flow} aria-label={t("How a work order moves")} data-testid="wo-flow">
      <ol className={f.steps}>
        {FLOW.map((s, i) => (
          <li key={s.status} className={f.item}>
            <Step
              {...(onPick ? { type: "button" as const, onClick: () => onPick(s.status) } : {})}
              className={f.step}
              data-state={current ? (i < at ? "done" : i === at ? "here" : "ahead") : undefined}
              data-active={active === s.status}
              aria-current={current === s.status ? "step" : undefined}
              data-testid={`wo-step-${s.status}`}
            >
              <span className={f.no}>{i < at ? "✓" : i + 1}</span>
              <span className={f.text}>
                <b>
                  {t(STATUS_LABEL[s.status])}
                  {counts && <em className={f.count}>{counts[s.status] ?? 0}</em>}
                </b>
                <small>{t(s.means)}</small>
                <small className={f.who}>{t(s.who)}</small>
              </span>
            </Step>
          </li>
        ))}
      </ol>
      <div className={f.side}>
        {SIDE.map((s) => {
          const Side = onPick ? "button" : "span";
          return (
            <Side
              key={s.status}
              {...(onPick ? { type: "button" as const, onClick: () => onPick(s.status) } : {})}
              className={f.sideItem}
              data-here={current === s.status}
              data-active={active === s.status}
            >
              <b>{t(STATUS_LABEL[s.status])}</b>
              {counts && <em className={f.count}>{counts[s.status] ?? 0}</em>} {t(s.means)}
            </Side>
          );
        })}
      </div>
      {current && (
        <p className={f.next} data-testid="wo-next">
          {next
            ? t("Next: {step} — {who}.", { step: t(STATUS_LABEL[next.status]), who: t(next.who) })
            : current === "cancelled"
              ? t("This order was canceled. Its history stays in the record.")
              : t("This order is closed. Its history stays in the record.")}
        </p>
      )}
    </section>
  );
}
